import React, { Dispatch, useEffect } from 'react';
import { Stack } from '@mui/material';
import Header from '../../General/Header/Header';
import ProjectAreaWarning from '../ProjectView/ProjectAreaWarning';
import { fullSize } from '../../../Constants';
import Footer from '../../General/Footer/Footer';
import ImportViewContent from './ImportViewContent';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { IAppReduxStore } from '../../../redux/store';
import { fetchDocuments, resetDocSelection } from '../../../util/ProjectUtils';
import {
    fetchAreas,
    fetchProjectDocTypes,
    fetchFileTypes,
    fetchImportViews,
    fetchImportDocuments,
    fetchImportStructure,
    fetchImportViewData,
    fetchProjectMetadata,
    fetchProjectViews,
    fetchImportDocTypes,
    fetchProjectDocStatus,
    resetImportArea,
} from '../../../util/ImportUtils';
import ImportViewControls from './ImportViewControls';
import ProjectAreaSelector from '../ProjectView/ProjectAreaSelector';
import DragPreview from '../../General/DragDrop/DragPreview';
import { ToastSeverity } from '../../General/Toast/Toast';
import {
    closeRemoveAdditionalDocDialog,
    resetRemoveAdditionalDocument,
    resetRemoveAdditionalDocumentsError,
} from '../../../redux/project/documents/sliceProjectAdditionalDocuments';
import { useEnqueueSnackbar } from '../../../hooks/useEnqueueSnackbar';
import { useTranslation } from 'react-i18next';
import { getProjectLogOpen } from '../../../redux/general/sliceDatabaseAccess';
import DialogsWrapper from '../../General/Dialogs/DialogsWrapper/DialogsWrapper';

export default () => {
    /** Get URL parameters from the react router. */
    const params = useParams();

    const { t } = useTranslation('translations', { keyPrefix: 'projectView' });

    /** Deconstruct params. */
    const { replicaId, areaName } = params;

    const dispatch: Dispatch<unknown> = useDispatch();

    const { canAccessDatabase } = useSelector((store: IAppReduxStore) => store.databaseAccess);
    const projectMetadata = useSelector((_store: IAppReduxStore) => _store.projectMetadata.data);
    const projectAreas = useSelector((_store: IAppReduxStore) => _store.projectAreas.data);
    const projectViews = useSelector((_store: IAppReduxStore) => _store.projectViews.data);
    const projectDocTypes = useSelector((_store: IAppReduxStore) => _store.projectDocTypes.data);
    const projectDocStatus = useSelector((_store: IAppReduxStore) => _store.projectDocStatus.data);
    const projectFileTypes = useSelector((_store: IAppReduxStore) => _store.projectFileTypes.data);
    const importViews = useSelector((_store: IAppReduxStore) => _store.importViews.data);
    const importViewData = useSelector(
        (_store: IAppReduxStore) => _store.importViewData.viewStructures.data,
    );
    const currentImportView = useSelector(
        (_store: IAppReduxStore) => _store.importData.currentView,
    );
    const selectedDocs = useSelector(
        (store: IAppReduxStore) => store.projectSelectedDocuments.storage.documentIds,
    );
    const dndIntent = useSelector((_store: IAppReduxStore) => _store.dragDrop.dndIntent);
    const fields = useSelector((_store: IAppReduxStore) => _store.importViewData.fields.data);
    const fieldSchema = useSelector(
        (_store: IAppReduxStore) => _store.importViewData.fieldSchema.data,
    );
    const { data: isAdditionalDocumentRemoved, error: errorRemoveAdditionalDocument } = useSelector(
        (store: IAppReduxStore) => store.projectAdditionalDocs.isAdditionalDocumentRemoved,
    );
    const { data: dynamicDialogsResponseData } = useSelector(
        (_store: IAppReduxStore) => _store.projectDocumentsDynamicSelection,
    );

    /** Hook for customizing Snackbar. */
    const enqueueSnackbar = useEnqueueSnackbar();

    useEffect(() => {
        if (errorRemoveAdditionalDocument) {
            enqueueSnackbar(t('errorRemoveAdditionalDocument'), {
                severity: ToastSeverity.Error,
            });

            dispatch(resetRemoveAdditionalDocumentsError());
        }
    }, [errorRemoveAdditionalDocument]);

    useEffect(() => {
        dispatch(getProjectLogOpen({ replicaId }));
    }, [replicaId]);

    useEffect(() => {
        if (canAccessDatabase) {
            !projectMetadata && fetchProjectMetadata(replicaId);
            !projectAreas && fetchAreas(replicaId);
            !projectViews && fetchProjectViews(replicaId);
            !projectDocTypes?.length && fetchProjectDocTypes(replicaId);
            !projectFileTypes?.length && fetchFileTypes(replicaId);
            !projectDocStatus?.length && fetchProjectDocStatus(replicaId);

            fetchImportViews(replicaId);
            fetchImportViewData(replicaId);
            fetchImportDocTypes(replicaId);
        }
    }, [canAccessDatabase]);

    useEffect(() => {
        if (fieldSchema?.length && fields?.length) {
            fetchImportDocuments(replicaId);
        }
    }, [fieldSchema, fields]);

    useEffect(() => {
        importViews?.forEach(view => {
            view.preload && fetchImportStructure(replicaId, view.name);
        });
    }, [importViews]);

    useEffect(() => {
        if (currentImportView && !importViewData[currentImportView]) {
            fetchImportStructure(replicaId, currentImportView);
        }
    }, [currentImportView]);

    useEffect(() => {
        resetDocSelection(params);
        resetImportArea();
    }, [replicaId, areaName]);

    useEffect(() => {
        if (isAdditionalDocumentRemoved) {
            enqueueSnackbar(t('toastRemoveAdditionalDocumentSuccess'), {
                severity: ToastSeverity.Success,
            });

            fetchDocuments(params);
            fetchImportViewData(replicaId);

            dispatch(resetRemoveAdditionalDocument());
            dispatch(closeRemoveAdditionalDocDialog());
        }
    }, [isAdditionalDocumentRemoved]);

    useEffect(() => {
        if (!dynamicDialogsResponseData?.refreshView) return;
        fetchDocuments(params);
    }, [dynamicDialogsResponseData]);

    return (
        <DialogsWrapper area='import'>
            <Header />
            <ProjectAreaWarning />
            <Stack direction='row' sx={{ ...fullSize, flexGrow: 4, overflow: 'hidden' }}>
                <ProjectAreaSelector />
                <Stack direction='column' sx={{ flex: 1, overflow: 'hidden' }}>
                    <ImportViewControls />
                    <ImportViewContent />
                    {dndIntent === 'documentCategorization' && (
                        <DragPreview dragCount={selectedDocs.length} />
                    )}
                </Stack>
            </Stack>
            <Footer />
        </DialogsWrapper>
    );
};
